import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Injectable,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { DcBaseComponent, UrlService } from '@datachain/ui-sdk/common';
import {
	ImageItem,
	UploadRequest,
	UploadStatus,
} from '@datachain/ui-sdk/components/image-picker';
import { Store } from '@ngrx/store';
import { Set } from 'immutable';
import {
	BehaviorSubject,
	forkJoin,
	interval,
	map,
	Observable,
	take,
	takeUntil,
	tap,
} from 'rxjs';

import { refreshPics } from '../../../core/store';
import { DcBaseRequester } from '../../../requesters';

const COLORS = [
	'#3d2a5c',
	'#1a3a2a',
	'#3a2e10',
	'#12244a',
	'#3a1a1a',
	'#2e1a30',
	'#12233a',
	'#2e2410',
	'#0e2030',
	'#241540',
	'#0e2818',
	'#3a1020',
];
const TCS = [
	'#c4a0f0',
	'#6ee7b7',
	'#fcd34d',
	'#93c5fd',
	'#fca5a5',
	'#f0abfc',
	'#7dd3fc',
	'#fde68a',
	'#67e8f9',
	'#d8b4fe',
	'#86efac',
	'#fda4af',
];

@Injectable({
	providedIn: 'root',
})
class DcPicUploaderRequester extends DcBaseRequester<unknown> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('PICTOGRAMMES_CREATE');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public uploadPic(label: string, pic: File): Observable<unknown> {
		const url = this.urlService.generateUrl(this.resource, {}, {});

		const formData = new FormData();
		formData.append('lib', label);
		formData.append('file', pic);
		return this.http.post<unknown>(url, formData);
	}
}

@Component({
	selector: 'app-image-picker',
	template: `
		<ng-container *ngIf="images$ | async; let allImages">
			<img
				*ngIf="lastSelected"
				class="image-selection"
				[src]="lastSelected.thumbnailUrl"
				[alt]="lastSelected.name" />
			<svg
				*ngIf="!lastSelected"
				class="image-empty"
				width="40"
				height="40"
				viewBox="0 0 24 24"
				fill="none"
				aria-hidden="true">
				<rect
					x="3"
					y="4"
					width="18"
					height="16"
					rx="2"
					stroke="currentColor"
					stroke-width="1.4" />
				<circle
					cx="8.5"
					cy="9"
					r="1.6"
					stroke="currentColor"
					stroke-width="1.4" />
				<path
					d="M4 17l4.5-4.5a1.5 1.5 0 0 1 2.1 0L16 18M14 16l1.8-1.8a1.5 1.5 0 0 1 2.1 0L21 16.5"
					stroke="currentColor"
					stroke-width="1.4"
					stroke-linecap="round"
					stroke-linejoin="round" />
			</svg>
			<button type="button" class="story-trigger" (click)="open = true">
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
					<rect
						x="1"
						y="1"
						width="5"
						height="5"
						rx="1"
						stroke="currentColor"
						stroke-width="1.3" />
					<rect
						x="8"
						y="1"
						width="5"
						height="5"
						rx="1"
						stroke="currentColor"
						stroke-width="1.3" />
					<rect
						x="1"
						y="8"
						width="5"
						height="5"
						rx="1"
						stroke="currentColor"
						stroke-width="1.3" />
					<rect
						x="8"
						y="8"
						width="5"
						height="5"
						rx="1"
						stroke="currentColor"
						stroke-width="1.3" />
				</svg>
				Select image
			</button>

			<dcds-image-picker
				[open]="open"
				[images]="allImages"
				[uploadProgress]="progress"
				[uploadStatus]="status"
				[disableClose]="disableClose"
				[backdrop]="backdrop"
				[backdropBlur]="backdropBlur"
				(upload)="onUpload($event)"
				(imageSelected)="onSelected($event)"
				(closed)="closeSelection()"></dcds-image-picker>
		</ng-container>
	`,
	styles: [
		`
			:host {
				--max-width: 150px;
				--border-rad: 6px;
				display: grid;
				row-gap: 0.5em;
				width: fit-content;
			}

			.image-selection {
				width: var(--max-width);
				aspect-ratio: 1;
				object-fit: cover;
				border-radius: var(--border-rad);
			}

			.image-empty {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 0.75rem;
				min-height: 100px;
				min-width: 100px;
				padding: 1rem;
				color: var(--image-picker-text-3);
				text-align: center;
				justify-self: center;
			}

			.story-trigger {
				width: var(--max-width);
				display: inline-flex;
				align-items: center;
				gap: 8px;
				padding: 10px 20px;
				background: #003366;
				color: #fff;
				border: none;
				border-radius: var(--border-rad);
				font: 500 14px sans-serif;
				cursor: pointer;
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppImagePickerComponent extends DcBaseComponent implements OnInit {
	@Input() public images: Array<ImageItem> = [];
	@Input() public failUpload = false;
	/** When false, the picker starts closed and is opened via the trigger button. */
	@Input() public startOpen = true;
	/** Forwarded to the picker — when true, backdrop clicks won't close it. */
	@Input() public disableClose = true;
	/** Forwarded — show the dark scrim tint. */
	@Input() public backdrop = true;
	/** Forwarded — blur behind the backdrop (only when backdrop=true). */
	@Input() public backdropBlur = true;

	@Input() public selection: number | null = -1;

	@Output() public selectedPic = new EventEmitter<ImageItem>();

	public open = true;
	public progress: number | null = null;
	public status: UploadStatus = 'idle';
	public lastSelected?: ImageItem;
	public uploadedRequest!: UploadRequest;

	public readonly images$ = new BehaviorSubject<Array<ImageItem>>([]);

	public constructor(
		private readonly picUploaderRequester: DcPicUploaderRequester,
		private readonly cdr: ChangeDetectorRef,
		private readonly store: Store
	) {
		super();
	}

	public ngOnInit(): void {
		this.open = this.startOpen;

		this.toObservable<Array<ImageItem>>('images')
			.pipe(
				map((images) => [
					{
						id: -1,
						thumbnailUrl: '/service/pictogramme_image?id=-1',
						name: 'test',
						color: 'red',
						tc: 'red',
						isNew: false,
					},
					...images,
				]),
				tap((images) => {
					this.lastSelected = this.getSelection(this.selection, images);
				})
			)
			.subscribe(this.images$);
	}

	public onSelected(image: ImageItem): void {
		this.lastSelected = image;
		this.selection = image.id as number;
		this.open = false;
		this.selectedPic.next(image);
	}

	public closeSelection(): void {
		this.open = false;
		this.lastSelected = this.getSelection(this.selection, this.images$.value);
	}

	public onUpload(req: UploadRequest): void {
		this.status = 'uploading';
		this.progress = 0;
		this.uploadedRequest = req;

		const upload$ = this.picUploaderRequester.uploadPic(
			req.label,
			this.uploadedRequest.file
		);
		const progress$ = interval(250).pipe(
			take(5),
			tap((i) => {
				this.progress = (i + 1) * 20;
				this.cdr.markForCheck();
			})
		);

		forkJoin({
			upload: upload$,
			progress: progress$,
		})
			.pipe(takeUntil(this.onDestroy$))
			.subscribe({
				next: () => {
					this.status = 'done';

					this.store.dispatch(
						refreshPics({
							recentUploads: Set([this.uploadedRequest.label]),
						})
					);

					const { length } = this.images$.value;
					const images = [
						...this.images$.value,
						{
							id: `new-${length}`,
							name: this.uploadedRequest.label,
							color: COLORS[length % COLORS.length],
							tc: TCS[length % TCS.length],
							thumbnailUrl: this.uploadedRequest.thumbnailDataUrl,
							isNew: true,
						},
					];
					this.images$.next(images);
				},
				error: (err) => {
					this.status = 'error';
					console.error(err);
				},
			});
	}

	private getSelection(
		selection: number | null,
		images: Array<ImageItem>
	): ImageItem | undefined {
		return selection
			? images.find((img) => img.id === selection)
			: {
					id: -1,
					thumbnailUrl: '/service/pictogramme_image?id=-1',
					name: 'test',
					color: 'red',
					tc: 'red',
					isNew: false,
			  };
	}
}
